import {
  Alert,
  AlertTitle,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { Check, Error, HelpOutline, Warning } from '@mui/icons-material'
import type { JSX } from 'react'

const Status = {
  IN_PROGRESS: -1,
  SUCCESS: 0,
  FAILURE: 1,
  WARNING: 2,
}

type ValidationData = {
  errors?: { code: number; descr: string }[]
  warnings?: { code: number; descr: string }[]
}

type Props = {
  description?: string
  label: string
  status: number
  validationData?: ValidationData
}

const CalibrationTestListItem = ({
  description,
  label,
  status,
  validationData,
}: Props): JSX.Element => {
  const getListIcon = (s: number) => {
    switch (s) {
      case Status.IN_PROGRESS:
        return <CircularProgress size={20} sx={{ color: 'primary.main' }} />
      case Status.SUCCESS:
        return <Check sx={{ color: 'success.main' }} />
      case Status.FAILURE:
        return <Error sx={{ color: 'error.main' }} />
      case Status.WARNING:
        return <Warning sx={{ color: 'warning.main' }} />
      default:
        return <HelpOutline />
    }
  }

  const renderAlertsAndWarnings = (data?: ValidationData) => {
    if (!data) return null

    const { errors, warnings } = data
    const hasErrors = Array.isArray(errors) && errors.length > 0
    const hasWarnings = Array.isArray(warnings) && warnings.length > 0

    if (!hasErrors && !hasWarnings) return null

    return (
      <List component="div" disablePadding>
        {hasErrors &&
          errors.map(({ code, descr }) => (
            <ListItem key={`error-${code}`}>
              <ListItemText
                primary={
                  <Alert severity="error">
                    <AlertTitle>{code}</AlertTitle>
                    {descr}
                  </Alert>
                }
              />
            </ListItem>
          ))}
        {hasWarnings &&
          warnings.map(({ code, descr }) => (
            <ListItem key={`warning-${code}`}>
              <ListItemText
                primary={
                  <Alert severity="warning">
                    <AlertTitle>{code}</AlertTitle>
                    {descr}
                  </Alert>
                }
              />
            </ListItem>
          ))}
      </List>
    )
  }

  return (
    <>
      <ListItem>
        <ListItemIcon sx={{ justifyContent: 'center' }}>
          {getListIcon(status)}
        </ListItemIcon>
        <ListItemText primary={label} secondary={description} />
      </ListItem>
      {renderAlertsAndWarnings(validationData)}
    </>
  )
}

export default CalibrationTestListItem
